import React from "react";
import { Bot, Loader2, Send } from "lucide-react";
import ChatBubble from "./ChatBubble";

/** Right-pane delivery-agent chat: transcript + composer. All state lives in
 *  the parent page; this component is presentational + emits onSend. */
export default function DeliveryChat({
  workspace,
  sending,
  error,
  draft,
  setDraft,
  onSend,
  scrollRef,
}) {
  return (
    <section
      data-testid="delivery-chat"
      className="rounded-3xl border border-sand-300 bg-sand-50 flex flex-col h-[78vh] min-h-[600px] overflow-hidden"
    >
      <div className="flex items-center gap-2 px-5 py-4 border-b border-sand-300/70">
        <span className="inline-flex h-7 w-7 grid place-items-center rounded-full bg-ink-deep text-sand-100">
          <Bot size={13} />
        </span>
        <p className="text-[13px] font-medium text-ink">
          Delivery Agent{workspace.model ? ` · ${workspace.model}` : ""}
        </p>
        <p className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
          {workspace.messages?.length || 0} msgs
        </p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5">
        {(workspace.messages || []).map((m, idx) => (
          <ChatBubble
            key={m.ts ? `${m.role}:${m.ts}:${idx}` : `${m.role}:${idx}:${(m.content || "").slice(0, 24)}`}
            role={m.role}
            content={m.content}
          />
        ))}
        {sending && <ChatBubble role="assistant" content="" pending />}
      </div>

      {error && (
        <p className="px-5 py-2 text-[12px] text-rust border-t border-rust/20 bg-rust/5">
          {error}
        </p>
      )}

      <form
        onSubmit={onSend}
        className="border-t border-sand-300/70 p-3 sm:p-4 flex gap-2 items-end"
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder="Continue the engagement — e.g. 'Move to Stage 4 and run Opportunity Selection.'"
          rows={2}
          data-testid="delivery-input"
          className="flex-1 rounded-xl border border-sand-300 bg-sand-100 px-4 py-3 text-[13.5px] text-ink placeholder:text-ink-muted/60 focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/20 transition-colors resize-none leading-relaxed"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sending}
          data-testid="delivery-send"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-5 py-3 text-[13px] font-medium hover:bg-rust transition-colors min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <>
              <Send size={13} /> Send
            </>
          )}
        </button>
      </form>
    </section>
  );
}
